import InvoiceGateway from "./invoice-gateway";
import Estimate from "./estimate";
import Invoice from "./invoice";
import formatDate from "@/helpers/formatDate";
import uuid from "uuid";
import BillingRuleApi from "@/orders/api/billing-rule-api";
import parseDate from "@/helpers/parseDate";
import Decimal from "decimal.js";
import UnitPrice from "@/orders/domain/models/unit-price";
import TotalPrice from "@/orders/domain/models/total-price";

export default class InvoiceService {
  public constructor(
    private invoices: InvoiceGateway,
    private billingRuleRepository: BillingRuleApi
  ) {}

  public async find(id: number): Promise<Invoice> {
    const invoice = await this.invoices.find(id);
    if (!invoice) {
      throw new Error("invoice is not found.");
    }
    return invoice;
  }

  public async save(invoice: Invoice): Promise<void> {
    await this.invoices.save(invoice);
  }

  /**
   * ステータスを変更する
   */
  public async changeStatus(id: string, status: string): Promise<void> {
    const invoice = await this.find(Number(id));
    invoice.status = status;
    await this.save(invoice);
  }

  public async invoice(estimate: Estimate): Promise<number> {
    const rule = await this.billingRuleRepository.findByProject(
      String(estimate.projectId)
    );
    const billingDate = new Date();
    const invoice = {
      estimateId: estimate.id,
      projectId: estimate.projectId,
      projectName: estimate.projectName,
      title: estimate.title,
      status: "created",
      total: estimate.total,
      subtotal: estimate.subtotal,
      tax: estimate.tax,
      taxRate: estimate.taxRate,
      billingDate: formatDate(billingDate),
      dueDate: formatDate(rule.getDueDate(billingDate)),
      bankAccount1: "",
      bankAccount2: "",
      bankAccount3: "",
      deposit: 0,
      depositRequestedAt: "",
      totalWithDeposit: estimate.total,
      items: estimate.items.map(i => ({
        id: uuid(),
        unitName: i.unitName,
        unitType: i.unitType,
        unitPrice: i.unitPrice,
        amount: i.amount,
        totalPrice: i.totalPrice,
        memo: i.memo,
        items: i.items.map(j => ({
          id: uuid(),
          unitName: j.unitName,
          unitType: j.unitType,
          unitPrice: j.unitPrice,
          amount: j.amount,
          totalPrice: j.totalPrice,
          memo: j.memo,
          items: []
        }))
      }))
    };
    return await this.invoices.save(invoice);
  }

  public async calculate(invoice: Invoice): Promise<Invoice> {
    const items = invoice.items.map(i => {
      if (i.items.length > 0) {
        // 小項目がある場合は小項目の合計を大項目の単価にする
        const items = i.items.map(j => ({
          ...j,
          totalPrice: new UnitPrice(j.unitPrice).items(j.amount).toNumber()
        }));
        const unitPrice = new UnitPrice(
          items
            .map(i => new Decimal(i.totalPrice))
            .reduce((sum, p) => sum.plus(p))
        );
        return {
          ...i,
          unitPrice: unitPrice.toNumber(),
          totalPrice: unitPrice.items(i.amount).toNumber(),
          items
        };
      } else {
        // 小項目がない場合は入力された単価から計算する
        return {
          ...i,
          totalPrice: new UnitPrice(i.unitPrice).items(i.amount).toNumber()
        };
      }
    });
    const total = new TotalPrice(
      items.map(i => new Decimal(i.totalPrice)).reduce((sum, p) => sum.plus(p))
    );
    return {
      ...invoice,
      items,
      subtotal: total.toNumber(),
      tax: total.tax(invoice.taxRate).toNumber(),
      total: total.withTax(invoice.taxRate).toNumber()
    };
  }

  public async destroy(id: number): Promise<void> {
    await this.invoices.destroy(id);
  }

  /**
   * 入金予定日を得る
   */
  public async getDueDate(
    billingDate: string,
    projectId: string
  ): Promise<string> {
    const date = parseDate(billingDate);
    const rule = await this.billingRuleRepository.findByProject(projectId);
    return formatDate(rule.getDueDate(date));
  }
}
